import { supabase } from "../lib/supabase";
import { SitemapService } from "./sitemapService";

export interface BlogPostDeletionResult {
  success: boolean;
  message: string;
  deletedPost?: {
    id: string;
    title: string;
    slug: string;
  };
  seoCleanup?: {
    seoOptimizedAt: string | null;
    seoOptimizationVersion: string | null;
  };
  sitemapUpdate?: {
    success: boolean;
    message: string;
  };
  errors?: string[];
}

export class BlogPostDeletionService {
  /**
   * Comprehensive blog post deletion with SEO cleanup and sitemap update
   */
  static async deleteBlogPost(postId: string): Promise<BlogPostDeletionResult> {
    const result: BlogPostDeletionResult = {
      success: false,
      message: "",
      errors: [],
    };

    try {
      console.log(
        `🗑️  Starting comprehensive deletion for blog post: ${postId}`,
      );

      // Step 1: Get post details before deletion
      const { data: post, error: fetchError } = await supabase
        .from("blog_posts")
        .select("id, title, slug, seo_optimized_at, seo_optimization_version")
        .eq("id", postId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch post details: ${fetchError.message}`);
      }

      if (!post) {
        throw new Error("Blog post not found");
      }

      console.log(`📄 Deleting blog post: "${post.title}" (${post.slug})`);

      // Step 2: Soft delete the blog post
      const { error: deleteError } = await supabase
        .from("blog_posts")
        .update({
          deleted: true,
          deleted_at: new Date().toISOString(),
          // Clear SEO optimization data
          seo_optimized_at: null,
          seo_optimization_version: null,
          // Clear other SEO fields
          meta_title: null,
          meta_description: null,
          meta_keywords: null,
          excerpt: null,
          image_url: null,
        })
        .eq("id", postId);

      if (deleteError) {
        throw new Error(`Failed to delete blog post: ${deleteError.message}`);
      }

      console.log(`✅ Blog post soft deleted successfully`);

      // Step 3: Clean up related SEO data
      const seoCleanup = await this.cleanupSEOData(postId, post);
      result.seoCleanup = seoCleanup;

      // Step 4: Update sitemap
      const sitemapUpdate = await this.updateSitemapAfterDeletion(post);
      result.sitemapUpdate = sitemapUpdate;

      // Step 5: Clean up related content (optional)
      await this.cleanupRelatedContent(postId);

      result.success = true;
      result.message = `Blog post "${post.title}" deleted successfully with SEO cleanup`;
      result.deletedPost = {
        id: post.id,
        title: post.title,
        slug: post.slug,
      };

      console.log(`✅ Comprehensive deletion completed for: ${post.title}`);
    } catch (error) {
      console.error("❌ Blog post deletion failed:", error);
      result.success = false;
      result.message =
        error instanceof Error ? error.message : "Unknown error occurred";
      result.errors?.push(result.message);
    }

    return result;
  }

  /**
   * Clean up SEO-related data for the deleted post
   */
  private static async cleanupSEOData(
    postId: string,
    post: any,
  ): Promise<{
    seoOptimizedAt: string | null;
    seoOptimizationVersion: string | null;
  }> {
    try {
      console.log(`🧹 Cleaning up SEO data for post: ${post.title}`);

      // Get SEO optimization history
      const seoOptimizedAt = post.seo_optimized_at;
      const seoOptimizationVersion = post.seo_optimization_version;

      if (seoOptimizedAt) {
        console.log(
          `📊 Found SEO optimization data: ${seoOptimizedAt} (v${seoOptimizationVersion})`,
        );

        // Log SEO cleanup for audit purposes
        console.log(`🗑️  SEO data cleared for post: ${post.title}`);
        console.log(`   - SEO optimized at: ${seoOptimizedAt}`);
        console.log(`   - SEO version: ${seoOptimizationVersion}`);
        console.log(`   - Meta title: cleared`);
        console.log(`   - Meta description: cleared`);
        console.log(`   - Meta keywords: cleared`);
        console.log(`   - Excerpt: cleared`);
        console.log(`   - Featured image: cleared`);
      }

      return {
        seoOptimizedAt,
        seoOptimizationVersion,
      };
    } catch (error) {
      console.error("❌ SEO cleanup failed:", error);
      return {
        seoOptimizedAt: null,
        seoOptimizationVersion: null,
      };
    }
  }

  /**
   * Update sitemap after blog post deletion
   */
  private static async updateSitemapAfterDeletion(post: any): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log(`🗺️  Updating sitemap after deletion of: ${post.title}`);

      // Trigger sitemap regeneration
      const sitemapResult = await SitemapService.triggerSitemapRegeneration();

      if (sitemapResult.success) {
        console.log(`✅ Sitemap regeneration triggered successfully`);
        console.log(
          `📋 Instructions: ${sitemapResult.instructions?.join(", ")}`,
        );

        return {
          success: true,
          message: "Sitemap regeneration triggered successfully",
        };
      } else {
        console.error(
          `❌ Sitemap regeneration failed: ${sitemapResult.message}`,
        );

        return {
          success: false,
          message: sitemapResult.message,
        };
      }
    } catch (error) {
      console.error("❌ Sitemap update failed:", error);

      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Sitemap update failed",
      };
    }
  }

  /**
   * Clean up related content (tags, categories, etc.)
   */
  private static async cleanupRelatedContent(postId: string): Promise<void> {
    try {
      console.log(`🧹 Cleaning up related content for post: ${postId}`);

      // Clean up blog tag relationships
      const { error: tagError } = await supabase
        .from("blog_tag_posts")
        .delete()
        .eq("blog_post_id", postId);

      if (tagError) {
        console.warn(`⚠️  Failed to clean up blog tags: ${tagError.message}`);
      } else {
        console.log(`✅ Blog tag relationships cleaned up`);
      }

      // Note: We don't delete categories or authors as they might be used by other posts
      // Only remove the specific relationships for this post
    } catch (error) {
      console.error("❌ Related content cleanup failed:", error);
    }
  }

  /**
   * Get deletion statistics
   */
  static async getDeletionStats(): Promise<{
    totalDeleted: number;
    recentlyDeleted: number;
    seoOptimizedDeleted: number;
  }> {
    try {
      const { data: deletedPosts, error } = await supabase
        .from("blog_posts")
        .select("id, title, deleted_at, seo_optimized_at")
        .eq("deleted", true)
        .order("deleted_at", { ascending: false });

      if (error) {
        console.error("Error fetching deletion stats:", error);
        return {
          totalDeleted: 0,
          recentlyDeleted: 0,
          seoOptimizedDeleted: 0,
        };
      }

      const totalDeleted = deletedPosts?.length || 0;
      const recentlyDeleted =
        deletedPosts?.filter((post) => {
          const deletedDate = new Date(post.deleted_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return deletedDate > thirtyDaysAgo;
        }).length || 0;
      const seoOptimizedDeleted =
        deletedPosts?.filter((post) => post.seo_optimized_at).length || 0;

      return {
        totalDeleted,
        recentlyDeleted,
        seoOptimizedDeleted,
      };
    } catch (error) {
      console.error("Error getting deletion stats:", error);
      return {
        totalDeleted: 0,
        recentlyDeleted: 0,
        seoOptimizedDeleted: 0,
      };
    }
  }

  /**
   * Restore a deleted blog post (undo deletion)
   */
  static async restoreBlogPost(
    postId: string,
  ): Promise<BlogPostDeletionResult> {
    try {
      console.log(`🔄 Restoring blog post: ${postId}`);

      // Get the deleted post
      const { data: post, error: fetchError } = await supabase
        .from("blog_posts")
        .select("id, title, slug")
        .eq("id", postId)
        .eq("deleted", true)
        .single();

      if (fetchError || !post) {
        throw new Error("Deleted blog post not found");
      }

      // Restore the post
      const { error: restoreError } = await supabase
        .from("blog_posts")
        .update({
          deleted: false,
          deleted_at: null,
        })
        .eq("id", postId);

      if (restoreError) {
        throw new Error(`Failed to restore blog post: ${restoreError.message}`);
      }

      // Update sitemap to include the restored post
      const sitemapUpdate = await this.updateSitemapAfterDeletion(post);

      console.log(`✅ Blog post restored: ${post.title}`);

      return {
        success: true,
        message: `Blog post "${post.title}" restored successfully`,
        deletedPost: {
          id: post.id,
          title: post.title,
          slug: post.slug,
        },
        sitemapUpdate,
      };
    } catch (error) {
      console.error("❌ Blog post restoration failed:", error);

      return {
        success: false,
        message: error instanceof Error ? error.message : "Restoration failed",
        errors: [error instanceof Error ? error.message : "Unknown error"],
      };
    }
  }
}
